import { CircularProgress } from '@material-ui/core'
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js'
import jwtDecode from 'jwt-decode'
import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation } from 'react-query'
import { useHistory } from 'react-router-dom'
import { authorize, createAccount } from '../../../Api'
import DatafinitiLogo from '../../HomeDR12/images/datafiniti-logo-white.svg'
import styles from './styles.module.css'
import BaseAlert from '../../Snackbar/NewImplementations/BaseAlert/BaseAlert'
import { submitToHubSpot } from '../../../Api/hubspot'

function Step4 ({ isActive, token, clearDfpItemsFromLocalStorage }) {
  const stripe = useStripe()
  const elements = useElements()
  const history = useHistory()
  const {
    register,
    handleSubmit,
    formState: { errors, isValid }
  } = useForm({
    mode: 'onChange'
  })
  const [isCardComplete, setIsCardComplete] = useState(false)
  const [loading, setLoading] = useState(false)
  const [userDataLogin, setUserDataLogin] = useState()
  const [parsedUserData, setParsedUserData] = useState({})
  const signupToken = token
  const localStorageData = localStorage.getItem('dfp/step2_data')

  const [alert, setAlert] = useState({
    open: false,
    title: '',
    body: '',
    color: '#48B966',
    actions: []
  })

  const redirectDashboard = () => {
    setTimeout(() => {
      setLoading(false)
      // Get plan IDs from local storage
      const userData = JSON.parse(localStorage.getItem('dfp/step2_data'))
      const planIds = userData?.plan_ids || []

      // Determine dashboard path based on plan types
      let basePath = '/property-data' // Default fallback
      const dataTypes = planIds.map(planId => planId.split('-')[0])

      if (dataTypes.includes('property')) {
        basePath = '/property-data'
      } else if (dataTypes.includes('people')) {
        basePath = '/people-data'
      } else if (dataTypes.includes('product')) {
        basePath = '/product-data'
      } else if (dataTypes.includes('business')) {
        basePath = '/business-data'
      }

      history.push(`${basePath}?signup=completed`)
      localStorage.removeItem('dfp/user-sign-up')
    }, 1500)
  }

  const redirectLogin = () => {
    setTimeout(() => {
      history.push('/login')
      clearDfpItemsFromLocalStorage()
    }, 2000)
  }

  useEffect(() => {
    const userData = JSON.parse(localStorageData)
    setParsedUserData(userData)

    if (signupToken && localStorageData) {
      const decodedToken = jwtDecode(signupToken)
      setUserDataLogin({ email: decodedToken.sub, password: userData.password })
    }
  }, [signupToken, localStorageData])

  const handleCardChange = event => {
    setIsCardComplete(event.complete)
  }

  const handleSuccess = data => {
    if (data.status >= 400) {
      if (data?.data?.errors) {
        setAlert({
          open: true,
          title: 'Error',
          body: data.data.errors[0],
          color: '#EF3688',
          actions: [
            {
              label: 'Ok',
              onClick: () => setAlert(a => ({ ...a, open: false }))
            }
          ]
        })

        redirectLogin()
      }
    } else {
      //   setShowSuccessMessage(true)
      setAlert({
        open: true,
        title: 'Account created successfully',
        body: 'Redirecting you to the Datafiniti portal...',
        color: '#48B966',
        actions: [
          { label: 'Ok', onClick: () => setAlert(a => ({ ...a, open: false })) }
        ]
      })
      login(userDataLogin)
    }
  }

  function handleError (error) {
    setAlert({
      open: true,
      title: 'Error',
      body: error.message || 'An unexpected error occurred.',
      color: '#EF3688',
      actions: [
        {
          label: 'Ok',
          onClick: () => setAlert(a => ({ ...a, open: false }))
        }
      ]
    })
    redirectLogin()
  }

  const [mutateAccount, { isLoading }] = useMutation(
    ({ jwt, userData }) => createAccount(jwt, userData),
    { onSuccess: handleSuccess, onError: handleError }
  )

  const [login, { isLoading: isLoginLoading }] = useMutation(
    userData => authorize(userData),
    {
      onSuccess: data => {
        localStorage.setItem('dfp/session', data?.data?.token)

        // Push GA4 event ONLY after successful account creation AND login
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          'event': 'form_submission_signup',
          'signup_type': 'paid'
        });

        redirectDashboard()
      },
      onError: handleError
    }
  )

  const onSubmit = async data => {
    setLoading(true)
    if (!stripe || !elements || !isCardComplete) {
      return
    }

    const cardElement = elements.getElement(CardElement)
    const { error, token } = await stripe.createToken(cardElement, {
      name: data.cardholderName,
      address_line1: data.address,
      address_city: data.city,
      address_state: data.state,
      address_zip: data.zipCode
    })

    if (error) {
      setAlert({
        open: true,
        title: 'Error',
        body: 'Something went wrong, contact our team',
        color: '#EF3688',
        actions: [
          {
            label: 'Ok',
            onClick: () => setAlert(a => ({ ...a, open: false }))
          }
        ]
      })
      setLoading(false)
      return
      
    } else {
      const planData = JSON.parse(localStorage.getItem('dfp/planData')) || {}
      const Gclid = planData.Gclid

      const userData = {
        first_name: data.cardholderName,
        last_name: parsedUserData.lastName,
        email: userDataLogin.email,
        organization: parsedUserData.organization,
        password: parsedUserData.password,
        payment_token: token.id,
        plan_ids: parsedUserData.plan_ids
      }

      if (Gclid) {
        userData.Gclid = Gclid
      }

      const dataTypes = [...new Set(parsedUserData.plan_ids.map(plan => plan.split('-')[0]))]
      const hubspotData = {
        email: userDataLogin.email,
        firstName: data.cardholderName,
        lastName: parsedUserData.lastName,
        signupPlans: parsedUserData.plan_ids.join(','),
        signupType: 'paid',
        dataType: dataTypes
      }
      await submitToHubSpot(hubspotData)
      mutateAccount({ jwt: signupToken, userData })
    }
  }


  return (
    <div className={styles.container}>
      <img
        src={DatafinitiLogo}
        alt='DatafinitiLogo'
        className={styles.logo}
        style={{ opacity: isActive ? 1 : 0 }}
      />
      <div>
        <h1 className={styles.heading}>Step 4: Payment</h1>
        <span className={styles.subHeading}>Enter payment information.</span>

        <form
          autoComplete='off'
          className={styles.form}
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className={styles.containerInputStep4}>
            <input
              type='text'
              className={styles.inputStep4}
              autoComplete='off'
              placeholder='Cardholder name'
              {...register('cardholderName', {
                required: 'Cardholder name is required'
              })}
              disabled={!isActive} // Disable if not active
            />
            {errors.cardholderName && (
              <p className={styles.error}>{errors.cardholderName.message}</p>
            )}
          </div>
          <div className={styles.containerInputStep4}>
            <input
              type='text'
              className={styles.inputStep4}
              autoComplete='off'
              placeholder='Address'
              {...register('address', { required: 'Address is required' })}
              disabled={!isActive} // Disable if not active
            />
            {errors.address && (
              <p className={styles.error}>{errors.address.message}</p>
            )}
          </div>
          <div className={styles.containerInputStep4}>
            <input
              type='text'
              className={styles.inputStep4}
              autoComplete='off'
              placeholder='City'
              {...register('city', { required: 'City is required' })}
              disabled={!isActive} // Disable if not active
            />
            {errors.city && (
              <p className={styles.error}>{errors.city.message}</p>
            )}
          </div>
          <div className={styles.containerInputStep4}>
            <input
              type='text'
              className={styles.inputStep4}
              autoComplete='off'
              placeholder='State'
              {...register('state', { required: 'State is required' })}
              disabled={!isActive} // Disable if not active
            />
            {errors.state && (
              <p className={styles.error}>{errors.state.message}</p>
            )}
          </div>
          <div className={styles.containerInputStep4}>
            <input
              type='text'
              className={styles.inputStep4}
              autoComplete='off'
              placeholder='Zip Code'
              {...register('zipCode', { required: 'Zip Code is required' })}
              disabled={!isActive} // Disable if not active
            />
            {errors.zipCode && (
              <p className={styles.error}>{errors.zipCode.message}</p>
            )}
          </div>
          <div
            className={
              !isActive
                ? styles.cardElementContainerDisabled
                : styles.cardElementContainer
            }
          >
            <CardElement
              options={{
                disableLink: true,
                hidePostalCode: true,
                iconStyle: 'solid',
                disabled: !isActive ? true : false,
                style: {
                  base: {
                    iconColor: '#CFF0FF',
                    fontFamily: 'Roboto',
                    fontSize: '20px',
                    fontWeight: '400',
                    color: '#FFFFFF', // Set the font color to white
                    '::placeholder': {
                      color: '#CFF0FF'
                    }
                  }
                }
              }}
              onChange={handleCardChange}
              // disabled={!isActive}
            />
          </div>
          {isLoading || isLoginLoading || loading ? (
            <button disabled className={styles.spinnerDisabledButton}>
              {' '}
              <CircularProgress className={styles.circularProgress} />
            </button>
          ) : (
            <button
              disabled={
                !isActive ||
                !isValid ||
                !isCardComplete ||
                loading ||
                isLoading ||
                isLoginLoading
              }
              className={styles.completeButton}
              type='submit'
            >
              Complete
            </button>
          )}
        </form>
      </div>

      <BaseAlert
        open={alert.open}
        // open={true}
        title={alert.title}
        body={alert.body}
        color={alert.color}
        style={{
          position: 'absolute',
          left: '50%',
          top: 'revert',
          bottom: 'calc(-153px + var(--alert-offset))',
          right: '0%',
          width: '320px',
          height: '84px',
          transform: 'translate(0, 0) !important'
        }}
        actions={alert.actions}
        onClose={() => setAlert(a => ({ ...a, open: false }))}
      />
    </div>
  )
}

export default Step4
